import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { asText } from '@prismicio/client';
import classNames from 'classnames';

import {
  Heading,
  Icon,
  Link,
  Product,
  ProductPlaceholder,
  Slider,
} from '@/components';
import {
  useSelectedStore,
  useShowByLocationSlug,
  useSpacingBetweenProductCard,
} from '@/hooks';
import { IProductRowSlice } from '@/types';
import { getProductsFromOpenSearchResponse, getSlicePadding } from '@/utils';
import { buildSearchBodyFromParams, SearchService } from '@/opensearch';

import styles from './productrow.module.scss';

const ProductsRow: FC<IProductRowSlice> = slice => {
  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaciBetween,
    spacing_between_card,
    isProductCardLargImage,
  } = useSpacingBetweenProductCard();
  const { store } = useSelectedStore();
  const { showContent } = useShowByLocationSlug(
    slice?.primary?.location_slug as string,
  );

  const initProducts = slice?.products ?? [];
  const selectedStore = store || slice?.selectedStore;
  const paddingSection = slice?.primary?.padding_section;
  const headerColor = slice?.primary?.header_color;
  const marginBottom = slice?.primary?.bottom_spacing;
  const backgroundColor = slice?.primary?.background_color;
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const seeAllHref = slice?.primary?.see_all_link;
  const seeMoreText = slice?.primary?.see_all_text ?? 'SEE ALL';
  const seeAllTextColor = slice?.primary?.see_all_text_color;
  const activeControlColor = slice?.primary?.active_control_color;
  const disableControlColor = slice?.primary?.disable_control_color;

  const isFilterByStore = slice?.primary?.filter_by_store;

  const ids = slice?.items?.map(item => item?.product_id?.trim()) as string[];

  const queryKey = ['searchProducts_product_row', store?.shortName, ...ids];

  const { search } = new SearchService(
    selectedStore?.msoStoreEntityId,
    selectedStore?.msoOrgId,
    undefined,
    selectedStore?.strict_medical_enabled,
  );

  const { data, isLoading } = useQuery({
    enabled: isFilterByStore,
    initialData: {
      products: initProducts,
    },
    queryKey,
    queryFn: async () => {
      const idParam = { id: ids };

      const { searchBody } = buildSearchBodyFromParams({
        searchParams: {
          ...idParam,
        },
      });

      const result = await search({ searchBody });

      const products = getProductsFromOpenSearchResponse(result);

      return { products };
    },
  });

  const productsCollection = data?.products;

  const controlSlideBorder = seeAllTextColor
    ? '1px solid transparent'
    : undefined;

  const background = seeAllTextColor ? 'transparent' : 'initial';

  if (!slice?.primary?.active || !productsCollection?.length || !showContent) {
    return null;
  }

  return (
    <section
      className={classNames(
        styles.product_row__section,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Slider
        activatedHover
        breakpoints={breakpoints}
        centeredSlides={false}
        className="products__row"
        freeMode
        isNotPaddingMobile={spacing_between_card === 'none'}
        seeAllElement={
          <Link
            aria-label={`Go to ${seeAllHref ?? '/deals'}`}
            className={styles.products__see_all}
            href={seeAllHref ?? '/deals'}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText} <span>({initProducts.length})</span>
          </Link>
        }
        showSliderCardLink={true}
        sliderHeading={
          <Heading level={2} style={{ color: headerColor! }}>
            {asText(slice?.primary?.products_header)}
          </Heading>
        }
        slidesPerView={defaultSlidePerView}
        spaceBetween={defaultSpaciBetween}
      >
        {isLoading
          ? new Array(20)
              .fill(null)
              .map((_, index) => (
                <ProductPlaceholder
                  key={index}
                  largeCard={isProductCardLargImage}
                />
              ))
          : productsCollection?.map(product => (
              <Product
                className={styles.slide__main}
                key={product.id}
                product={product}
              />
            ))}
        <Link
          aria-label={`Go to ${seeAllHref ?? '/deals'}`}
          className={classNames(
            styles.slide__main,
            styles.product__shop_all_slider,
          )}
          href={seeAllHref ?? '/deals'}
          style={{
            color: seeAllTextColor!,
            background: background,
          }}
        >
          <Icon name="right" />
          <p
            style={{
              color: seeAllTextColor!,
            }}
          >
            {seeMoreText}
          </p>
        </Link>
      </Slider>
      <style>
        {`.products__row {
            .slick-arrow {
              color: ${activeControlColor!};
              border: ${controlSlideBorder};

              &:hover {
                color: ${activeControlColor!};
              }

              &.slick-prev {
                border: ${controlSlideBorder};

                &::after {
                  border-right: ${controlSlideBorder};
                }
              }

              &.slick-disabled {
                color: ${disableControlColor}
              }
            }
          }
        `}
      </style>
    </section>
  );
};
export default ProductsRow;
